import {
    createError,
    EntityChanged,
    RelodeApiError,
    useNotifications,
    useRelodeFormik,
} from "@relode/core";
import { profileService } from "../../../shared/backend/controllers/settings/profileService";
import { convertClientProfileResponse } from "../../../shared/backend/converters/convertClientProfileResponse";
import { convertClientProfileUpdateRequest } from "../../../shared/backend/converters/convertClientProfileUpdateRequest";
import { IClientProfile } from "apps/client/src/shared/types/profile/IClientProfile";
import * as React from "react";
import { creators, SettingsActionTypes } from "./settingsActionTypes";

export const useSettingsActions = (
    dispatch: React.Dispatch<SettingsActionTypes>
) => {
    const { actions: notifications } = useNotifications();
    const { formik__infra } = useRelodeFormik<IClientProfile>();

    const fetchSettings = async () => {
        dispatch(creators.fetchSettings());
        try {
            const loadClientProfileResponse = await profileService.getClientProfile();

            const client = convertClientProfileResponse(
                loadClientProfileResponse
            );

            formik__infra.resetForm({ values: client });
            dispatch(creators.fetchSettingsSuccess(client));
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    dispatch(creators.fetchSettingsFailure(err));
                    return;
                }
            }
            throw createError(err, "settingsActions.fetchSettings");
        }
    };

    const saveProfile = async (profile: IClientProfile) => {
        dispatch(creators.saveSettings());
        try {
            const request = convertClientProfileUpdateRequest(profile);

            const result = await profileService.saveClientProfile(request);

            const updatedContact = convertClientProfileResponse(result);
            notifications.showSnack("success", "saved information");
            formik__infra.resetForm({ values: updatedContact });
            dispatch(creators.saveSettingsSuccess());
        } catch (err) {
            if (err instanceof RelodeApiError) {
                if (err.isAbort()) return;
                if (err.isClientError()) {
                    notifications.showSnack(
                        "error",
                        "error saving information"
                    );

                    err.validationErrors?.forEach(error => {
                        const message = error.messages.join(" ");
                        formik__infra.setFieldError(
                            error.field as any,
                            message
                        );
                        formik__infra.setFieldTouched(error.field as any);
                    });
                    dispatch(creators.saveSettingsFailure(err));
                    return;
                }
            }
            throw createError(err, "settingsActions.saveProfile");
        }
    };

    const setCurrentTab = (tab: string) => {
        dispatch(creators.setCurrentTab(tab));
    };

    const setClientProfileChanged = (
        changed: EntityChanged<IClientProfile>
    ) => {
        dispatch(creators.setContactChanged(changed));
    };

    return {
        saveProfile,
        fetchSettings,
        setCurrentTab,
        setClientProfileChanged,
    };
};

export type SettingsActions = ReturnType<typeof useSettingsActions>;
